import { storage as localStorage } from '../utils/storage'
import { LOCAL_STORAGE_KEY_READ } from './useReadItems'

/** Maximum number of viewed items to store in local storage (FIFO) */
const VIEWED_ITEMS_LIMIT = 500
/** Local storage key for the viewed items list */
export const LOCAL_STORAGE_KEY_VIEWED = 'app:viewedItems'

// Singleton state: in-memory cache initialized once per app session
let viewedItemsCache: Set<string> | null = null
let migrationComplete = false

let previousItems: string[] = []

try {
  const parsed = JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_KEY_VIEWED) || '[]'
  )
  previousItems = Array.isArray(parsed) ? parsed : []
} catch {
  previousItems = []
}

const previousPageCache: Set<string> = new Set(
  previousItems.filter((id) => !!id.trim())
)

/**
 * Initializes the viewed items cache from localStorage
 * Runs exactly once per app session (singleton pattern)
 */
function initializeCache(): Set<string> {
  if (viewedItemsCache !== null) {
    return viewedItemsCache
  }

  const storedValue = localStorage.getItem(LOCAL_STORAGE_KEY_VIEWED)
  if (!storedValue) {
    viewedItemsCache = new Set()
    return viewedItemsCache
  }

  try {
    const list = JSON.parse(storedValue)
    viewedItemsCache = new Set(Array.isArray(list) ? list : [])
  } catch (error) {
    console.error('Failed to parse viewed items from localStorage:', error)
    viewedItemsCache = new Set()
  }

  return viewedItemsCache
}

/**
 * One-time migration from legacy read items storage
 * Runs exactly once per app session
 */
function migrateFromReadItems() {
  if (migrationComplete) return
  migrationComplete = true

  const cache = initializeCache()

  // Only migrate if we have no viewed items yet
  if (cache.size === 0) {
    const readValue = localStorage.getItem(LOCAL_STORAGE_KEY_READ)
    if (readValue) {
      try {
        const legacyReadList = JSON.parse(readValue)
        if (Array.isArray(legacyReadList) && legacyReadList.length > 0) {
          legacyReadList.forEach((id) => {
            cache.add(id)
            // Keep previous-page cache in sync so deranking honors legacy state
            // immediately during the first session after migration.
            previousPageCache.add(id)
          })
          persistCache(cache)
        }
      } catch (e) {
        console.error('Failed to migrate app:readItems to app:viewedItems', e)
      }
    }
  }
}

/**
 * Persists the cache to localStorage with error handling
 * @param cache - Set of viewed item IDs
 */
function persistCache(cache: Set<string>) {
  try {
    const list = Array.from(cache).slice(-VIEWED_ITEMS_LIMIT)
    localStorage.setItem(LOCAL_STORAGE_KEY_VIEWED, JSON.stringify(list))
  } catch (error) {
    // Handle QuotaExceededError or other storage exceptions
    console.error('Failed to save viewed items to localStorage:', error)
  }
}

/**
 * Composable for managing article view tracking (feed visibility)
 *
 * Provides functionality to:
 * - Track which articles have been seen in the feed (via Intersection Observer)
 * - Store viewed state in local storage (limit 500 items)
 * - Use in-memory cache for O(1) lookups during sorting
 *
 * @returns Object containing viewed tracking methods
 */
export const useViewedItems = () => {
  // Initialize cache and run migration on first use
  if (typeof window !== 'undefined' && !migrationComplete) {
    migrateFromReadItems()
  }

  const cache = initializeCache()

  /**
   * Adds an item to the viewed list
   * - Skips if item already exists
   * - Adds new items to the cache
   * - Enforces FIFO limit when persisting
   */
  const addViewedItem = (id: string) => {
    if (!cache.has(id)) {
      cache.add(id)

      // If we exceed the limit, remove the oldest items (FIFO)
      while (cache.size > VIEWED_ITEMS_LIMIT) {
        const oldest = cache.values().next().value
        if (oldest !== undefined) {
          cache.delete(oldest)
        } else {
          break
        }
      }

      persistCache(cache)
    }
  }

  /**
   * Checks if an item has been marked as viewed
   * O(1) lookup using in-memory Set
   */
  const isItemViewed = (id: string): boolean => {
    return cache.has(id)
  }

  const isItemPreviouslyViewed = (id: string): boolean => {
    return !!id.trim() && previousPageCache.has(id)
  }

  /**
   * Sorts an array of items by viewed status
   * - Unviewed items appear first
   * - Viewed items appear last
   * - Maintains original order within each group
   */
  const sortItemsByViewedStatus = <T extends { id: string }>(
    items: T[]
  ): T[] => {
    return items.slice().sort((a, b) => {
      const aViewed = isItemViewed(a.id)
      const bViewed = isItemViewed(b.id)
      return aViewed === bViewed ? 0 : aViewed ? 1 : -1
    })
  }

  return {
    addViewedItem,
    isItemViewed,
    isItemPreviouslyViewed,
    sortItemsByViewedStatus,
  }
}
